import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as OrganizationAccountsService from '../services/OrganizationAccountsService';
import { OrganizationAccount } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const organizationAccountsKeys = {
    all: ['organizationAccounts'] as const,
    lists: () => [...organizationAccountsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...organizationAccountsKeys.lists(), { filters }] as const,
    details: () => [...organizationAccountsKeys.all, 'detail'] as const,
    detail: (id: string) => [...organizationAccountsKeys.details(), id] as const,
};

const DEFAULT_LIMIT = 10;

// Hooks
// FNP-562: token pagination. `filters` is the ONLY variable that changes the
// result set, and it is in the queryKey via `list(filters)` — omitting it would
// serve the previous organization's accounts from cache after a route change.
export const useOrganizationAccountsQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        queryKey: organizationAccountsKeys.list(filters),
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            OrganizationAccountsService.fetchRecords({
                filter: filters,
                limit: DEFAULT_LIMIT,
                nextToken: pageParam,
            }),
        // Must be `undefined`, never `null` and never the same token — either of
        // those makes React Query believe another page exists forever and
        // refetch without end.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

export const useOrganizationAccountQuery = (id: string) => {
    return useQuery({
        queryKey: organizationAccountsKeys.detail(id),
        queryFn: () => OrganizationAccountsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateOrganizationAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: OrganizationAccountsService.add,
        onSuccess: () => {
            notifySuccess('Organization account created');
            queryClient.invalidateQueries({ queryKey: organizationAccountsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateOrganizationAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: OrganizationAccount }) =>
            OrganizationAccountsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Organization account updated');
            queryClient.invalidateQueries({ queryKey: organizationAccountsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: organizationAccountsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeactivateOrganizationAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: OrganizationAccountsService.deactivate,
        onSuccess: () => {
            notifySuccess('Organization account deactivated');
            queryClient.invalidateQueries({ queryKey: organizationAccountsKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useOrganizationAccountsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
